import React from 'react';
import { X, Calendar, MapPin, Euro, Building, ExternalLink, Users, FileText } from 'lucide-react';

interface TenderDetails {
  id: string;
  titolo: string;
  comune: string;
  importo: number;
  data_pubblicazione: string;
  data_scadenza: string;
  stato: string;
  tipo: string;
  descrizione?: string;
  url?: string;
  cig?: string;
  participants?: Array<{
    company_name: string;
    amount: number;
    position?: number;
  }>;
}

interface TenderModalProps {
  tender: TenderDetails | null;
  isOpen: boolean;
  onClose: () => void;
}

const TenderModal: React.FC<TenderModalProps> = ({ tender, isOpen, onClose }) => {
  if (!isOpen || !tender) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'aperto':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'chiuso':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'in_corso':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Dettagli Bando
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Title and Status */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {tender.titolo}
                </h3>
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tender.stato)}`}>
                    {tender.stato}
                  </span>
                  {tender.cig && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      CIG: {tender.cig}
                    </span>
                  )}
                </div>
              </div>
              {tender.url && (
                <a
                  href={tender.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <ExternalLink className="h-5 w-5" />
                  <span>Vai al bando</span>
                </a>
              )}
            </div>

            {/* Basic Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Comune</span>
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {tender.comune}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Euro className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Importo</span>
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(tender.importo)}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Pubblicazione</span>
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatDate(tender.data_pubblicazione)}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Scadenza</span>
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatDate(tender.data_scadenza)}
                </div>
              </div>
            </div>

            {/* Type */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Building className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Tipo</span>
              </div>
              <div className="text-gray-900 dark:text-white">
                {tender.tipo}
              </div>
            </div>

            {/* Description */}
            {tender.descrizione && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Descrizione</span>
                </div>
                <div className="text-gray-900 dark:text-white whitespace-pre-wrap">
                  {tender.descrizione}
                </div>
              </div>
            )}

            {/* Participants */}
            {tender.participants && tender.participants.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-4">
                  <Users className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Partecipanti ({tender.participants.length})
                  </span>
                </div>
                <div className="space-y-3">
                  {Array.isArray(tender.participants) ? tender.participants.map((participant, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border"
                    >
                      <div className="flex items-center space-x-3">
                        {participant.position && (
                          <div className="flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                            {participant.position}
                          </div>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {participant.company_name}
                        </span>
                      </div>
                      <span className="text-gray-600 dark:text-gray-400">
                        {formatCurrency(participant.amount)}
                      </span>
                    </div>
                  )) : null}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenderModal;